<template>
  <div class="reference-card">
    <CitationInfoLabel :label="index" :source="sourceData" show-icon />

    <div class="reference-content">
      <ReferenceDetailsLabels :source="sourceData" :doc-type="docType" />

      <span>{{ reference.text }}</span>

      <ExpansionPanel v-model="isExpanded" class="referenced-panel" :title="t('common.details')">
        <ReferenceDetails :reference="reference" @preview="previewDocument" />
      </ExpansionPanel>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ExpansionPanel from '~/components/common/ExpansionPanel';
import WarningPopup from '~/components/common/WarningPopup';
import DocumentCard from '~/components/conversation/chatMessage/DocumentCard';
import CitationInfoLabel from '~/components/conversation/chatMessage/CitationInfoLabel.vue';
import ReferenceDetails from '~/components/conversation/referencePanel/ReferenceDetails.vue';
import ReferenceDetailsLabels from '~/components/conversation/referencePanel/ReferenceDetailsLabels.vue';
import BaseImage from '~/components/common/BaseImage.vue';
import useDocuments from '~/composables/useDocuments';
import useModals from '~/composables/useModals';
import { getCitationSourceData } from '~/utils/citations-utils';

export default {
  components: {
    BaseImage,
    CitationInfoLabel,
    DocumentCard,
    ExpansionPanel,
    ReferenceDetails,
    ReferenceDetailsLabels,
    WarningPopup,
  },
  props: {
    index: { type: Number, default: 0 },
    reference: { type: Object, default: () => ({}) },
  },
  setup(props) {
    const { t } = useI18n();
    const { setSelectedDocument } = useDocuments();
    const { showPreviewDocument } = useModals();
    const isExpanded = ref(false);
    const isUnsubstantiated = computed(
      () => props.reference.unsubstantiated || props.reference.passages?.some((passage) => passage.unsubstantiated),
    );
    const sourceData = computed(() => {
      if (isUnsubstantiated.value) {
        return { ...getCitationSourceData(props.reference.externalId), unsubstantiated: true };
      }

      return getCitationSourceData(props.reference.externalId) || {};
    });
    const docType = computed(() => props.reference.doc_type || props.reference.passages?.[0]?.doc_type || '');

    const previewDocument = async (page) => {
      await setSelectedDocument({ reference: props.reference });
      showPreviewDocument(page);
    };

    return {
      isExpanded,
      isUnsubstantiated,
      previewDocument,
      sourceData,
      docType,
      t,
    };
  },
};
</script>

<style lang="scss" scoped>
.reference-card {
  background-color: $bb-neutral-0;
  border-radius: 8px;
  padding: $bb-spacing-2;
  display: flex;
  gap: $bb-spacing-3;

  .reference-content {
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-3;
  }

  .referenced-panel {
    :deep(.v-expansion-panel) {
      background-color: $bb-neutral-0;
      padding: 0;
    }

    :deep(.v-expansion-panel-title) {
      width: fit-content;
      min-height: unset;
    }

    :deep(.body-2) {
      @include bb-regular-text-styles('md');
    }
  }
}
</style>
